import React, { useEffect, useMemo, useRef, useState } from "react";

const PROCESS_STORAGE_KEY = "yoma_process";

const BG = "#F8F4EE";
const BG_CARD = "#FFFFFF";
const GOLD = "#C09A5B";
const TAUPE = "#3D2E20";
const MUTED = "#8B7361";
const BORDER = "#D8C9B8";

const FF_PLAYFAIR = "'Playfair Display', serif";
const FF_DM = "'DM Sans', sans-serif";

const CATEGORIES_OPTIONS = [
  "Check-list démarrage projet",
  "Check-list fin de chantier",
  "Simulation de crise",
  "Manuel opérationnel",
  "Référencement constructeur",
];

const LINE_CLAMP_2 = {
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

export default function ProcessYomaPage({ cabinetId = null }) {
  const [processItems, setProcessItems] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);

  const [form, setForm] = useState({
    categorie: "Check-list démarrage projet",
    titre: "",
    contenu: "",
  });

  const [filterCategorie, setFilterCategorie] = useState("toutes");
  const [search, setSearch] = useState("");
  const [viewing, setViewing] = useState(null);
  const formSectionRef = useRef(null);

  const [companyName, setCompanyName] = useState("MON CABINET");

  useEffect(() => {
    try {
      const n = localStorage.getItem("yoma_company_name");
      if (n) setCompanyName(n);
    } catch (_) {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(PROCESS_STORAGE_KEY);
      if (saved) {
        setProcessItems(JSON.parse(saved));
      }
    } catch (error) {
      console.error("Erreur chargement process YOMA:", error);
    } finally {
      setIsLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (!isLoaded) return;

    try {
      localStorage.setItem(PROCESS_STORAGE_KEY, JSON.stringify(processItems));
    } catch (error) {
      console.error("Erreur sauvegarde process YOMA:", error);
    }
  }, [processItems, isLoaded]);

  function updateField(field, value) {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  function ajouterFiche() {
    if (!form.titre || !form.contenu) return;

    const nouvelleFiche = {
      id: Date.now(),
      ...form,
      dateCreation: new Date().toLocaleDateString("fr-FR"),
    };

    setProcessItems((prev) => [...prev, nouvelleFiche]);

    setForm({
      categorie: "Check-list démarrage projet",
      titre: "",
      contenu: "",
    });
  }

  function supprimerFiche(id) {
    setProcessItems((prev) => prev.filter((item) => item.id !== id));
    if (viewing && viewing.id === id) setViewing(null);
  }

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    return processItems.filter((item) => {
      if (filterCategorie !== "toutes" && item.categorie !== filterCategorie) return false;
      if (!q) return true;
      const titre = (item.titre || "").toLowerCase();
      const contenu = (item.contenu || "").toLowerCase();
      const cat = (item.categorie || "").toLowerCase();
      return titre.includes(q) || contenu.includes(q) || cat.includes(q);
    });
  }, [processItems, filterCategorie, search]);

  const groupedByCategory = useMemo(() => {
    const map = {};
    filteredItems.forEach((item) => {
      const c = item.categorie || "Autre";
      if (!map[c]) map[c] = [];
      map[c].push(item);
    });
    const order = [...CATEGORIES_OPTIONS, ...Object.keys(map).filter((k) => !CATEGORIES_OPTIONS.includes(k))];
    const seen = new Set();
    const keys = [];
    order.forEach((k) => {
      if (map[k] && !seen.has(k)) {
        seen.add(k);
        keys.push(k);
      }
    });
    Object.keys(map).forEach((k) => {
      if (!seen.has(k)) keys.push(k);
    });
    return keys.map((k) => ({ categorie: k, items: map[k] }));
  }, [filteredItems]);

  const kpiTotal = processItems.length;
  const kpiCategoriesActives = useMemo(() => {
    const s = new Set(processItems.map((i) => i.categorie).filter(Boolean));
    return s.size;
  }, [processItems]);

  const kpiDerniereMaj = useMemo(() => {
    if (!processItems.length) return "—";
    const maxId = Math.max(...processItems.map((i) => Number(i.id) || 0));
    try {
      return new Date(maxId).toLocaleString("fr-FR", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "—";
    }
  }, [processItems]);

  const isTrulyEmpty = processItems.length === 0;
  const hasNoFilterResults = !isTrulyEmpty && filteredItems.length === 0;

  function scrollToNewForm() {
    formSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  const inputBase = {
    width: "100%",
    border: `1px solid ${BORDER}`,
    borderRadius: 10,
    padding: "12px 14px",
    background: BG_CARD,
    fontFamily: FF_DM,
    fontSize: 14,
    color: TAUPE,
    outline: "none",
    boxSizing: "border-box",
  };

  if (cabinetId == null) {
    return <div style={{ padding: 24, textAlign: "center", color: "#8B7361" }}>Chargement...</div>;
  }

  return (
    <><style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;700&family=DM+Sans:wght@400;500;600;700&display=swap');
        `}
      </style><main
        style={{
          minHeight: "100%",
          background: BG,
          fontFamily: FF_DM,
          color: TAUPE,
          padding: "28px 24px 48px",
          boxSizing: "border-box",
        }}
      ><div style={{ maxWidth: 1100, margin: "0 auto" }}>
          {/* HEADER */}
          <header style={{ marginBottom: 28 }}><div
              style={{
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: MUTED,
                marginBottom: 8,
              }}
            >
              {companyName}
            </div><h1
              style={{
                margin: 0,
                fontFamily: FF_PLAYFAIR,
                fontSize: 32,
                fontWeight: 700,
                color: TAUPE,
                lineHeight: 1.15,
              }}
            >
              Process YOMA
            </h1><p style={{ margin: "10px 0 0", fontSize: 15, color: MUTED, maxWidth: 520 }}>
              Guide métier, procédures et check-lists intégrés
            </p></header>

          {/* KPIs */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 14,
              marginBottom: 28,
            }}
          ><div
              style={{
                background: BG_CARD,
                borderRadius: 14,
                padding: "18px 20px",
                border: `1px solid ${BORDER}`,
                boxShadow: "0 4px 20px rgba(61,46,32,0.06)",
              }}
            ><div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", color: MUTED, marginBottom: 6 }}>
                Total fiches process
              </div><div style={{ fontFamily: FF_PLAYFAIR, fontSize: 28, fontWeight: 700, color: TAUPE }}>{kpiTotal}</div></div><div
              style={{
                background: BG_CARD,
                borderRadius: 14,
                padding: "18px 20px",
                border: `1px solid rgba(192,154,91,0.45)`,
                boxShadow: "0 4px 20px rgba(192,154,91,0.12)",
              }}
            ><div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", color: MUTED, marginBottom: 6 }}>
                Catégories actives
              </div><div style={{ fontFamily: FF_PLAYFAIR, fontSize: 28, fontWeight: 700, color: GOLD }}>{kpiCategoriesActives}</div></div><div
              style={{
                background: BG_CARD,
                borderRadius: 14,
                padding: "18px 20px",
                border: `1px solid ${BORDER}`,
                boxShadow: "0 4px 20px rgba(61,46,32,0.06)",
              }}
            ><div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", color: MUTED, marginBottom: 6 }}>
                Dernière mise à jour
              </div><div style={{ fontFamily: FF_DM, fontSize: 15, fontWeight: 600, color: MUTED }}>{kpiDerniereMaj}</div></div></div>

          {/* NOUVELLE FICHE */}
          <section
            ref={formSectionRef}
            style={{
              background: BG_CARD,
              borderRadius: 16,
              padding: "22px 24px 20px",
              border: `1px solid ${BORDER}`,
              marginBottom: 28,
              boxShadow: "0 6px 28px rgba(61,46,32,0.07)",
            }}
          ><h2
              style={{
                margin: "0 0 18px",
                fontFamily: FF_PLAYFAIR,
                fontSize: 22,
                fontWeight: 700,
                color: TAUPE,
              }}
            >
              Nouvelle fiche process
            </h2><div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}><div><label style={{ display: "block", fontSize: 11, fontWeight: 600, color: MUTED, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  Catégorie
                </label><select
                  value={form.categorie}
                  onChange={(e) => updateField("categorie", e.target.value)}
                  style={{ ...inputBase, cursor: "pointer" }}
                >
                  {CATEGORIES_OPTIONS.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select></div><div><label style={{ display: "block", fontSize: 11, fontWeight: 600, color: MUTED, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  Titre de la fiche
                </label><input
                  placeholder="Ex. — Validation plans avant travaux"
                  value={form.titre}
                  onChange={(e) => updateField("titre", e.target.value)}
                  style={inputBase}
                /></div></div><div style={{ marginBottom: 18 }}><label style={{ display: "block", fontSize: 11, fontWeight: 600, color: MUTED, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                Contenu / procédure
              </label><textarea
                placeholder="Décrivez la procédure, les étapes, la check-list…"
                rows={5}
                value={form.contenu}
                onChange={(e) => updateField("contenu", e.target.value)}
                style={{ ...inputBase, resize: "vertical", minHeight: 120, lineHeight: 1.45 }}
              /></div><div style={{ display: "flex", justifyContent: "flex-end" }}><button
                type="button"
                onClick={ajouterFiche}
                style={{
                  padding: "12px 22px",
                  borderRadius: 10,
                  border: "none",
                  background: `linear-gradient(135deg, ${GOLD} 0%, #A8844A 100%)`,
                  color: "#FFF",
                  fontFamily: FF_DM,
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: "pointer",
                  boxShadow: "0 4px 14px rgba(192,154,91,0.45)",
                }}
              >
                Ajouter la fiche
              </button></div></section>

          {/* FILTRES */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 12,
              alignItems: "flex-end",
              marginBottom: 20,
            }}
          ><div style={{ flex: "1 1 200px", maxWidth: 280 }}><label style={{ display: "block", fontSize: 11, fontWeight: 600, color: MUTED, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                Catégorie
              </label><select
                value={filterCategorie}
                onChange={(e) => setFilterCategorie(e.target.value)}
                style={{ ...inputBase, cursor: "pointer" }}
              ><option value="toutes">Toutes les catégories</option>
                {CATEGORIES_OPTIONS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select></div><div style={{ flex: "2 1 280px" }}><label style={{ display: "block", fontSize: 11, fontWeight: 600, color: MUTED, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                Recherche
              </label><input
                placeholder="Rechercher dans titres et contenus…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={inputBase}
              /></div></div>

          {/* BIBLIOTHÈQUE */}
          <section><h2
              style={{
                margin: "0 0 18px",
                fontFamily: FF_PLAYFAIR,
                fontSize: 24,
                fontWeight: 700,
                color: TAUPE,
              }}
            >
              Bibliothèque process
            </h2>

            {isTrulyEmpty && (
              <div
                style={{
                  textAlign: "center",
                  padding: "56px 24px",
                  background: BG_CARD,
                  borderRadius: 16,
                  border: `1px dashed ${BORDER}`,
                }}
              ><p style={{ margin: "0 0 20px", fontFamily: FF_PLAYFAIR, fontSize: 20, color: TAUPE }}>
                  Aucune fiche — créez votre première procédure
                </p><button
                  type="button"
                  onClick={scrollToNewForm}
                  style={{
                    padding: "12px 24px",
                    borderRadius: 10,
                    border: "none",
                    background: `linear-gradient(135deg, ${GOLD} 0%, #A8844A 100%)`,
                    color: "#FFF",
                    fontFamily: FF_DM,
                    fontSize: 14,
                    fontWeight: 700,
                    cursor: "pointer",
                    boxShadow: "0 4px 14px rgba(192,154,91,0.4)",
                  }}
                >
                  Créer une fiche
                </button></div>
            )}

            {hasNoFilterResults && (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px 24px",
                  background: BG_CARD,
                  borderRadius: 16,
                  border: `1px solid ${BORDER}`,
                  color: MUTED,
                  fontSize: 15,
                }}
              >
                Aucun résultat pour ces filtres. Essayez d’élargir la recherche ou la catégorie.
              </div>
            )}

            {!isTrulyEmpty &&
              groupedByCategory.map(({ categorie, items }) => (
                <div key={categorie} style={{ marginBottom: 28 }}><div
                    style={{
                      fontFamily: FF_PLAYFAIR,
                      fontSize: 18,
                      fontWeight: 700,
                      color: TAUPE,
                      marginBottom: 12,
                      paddingBottom: 8,
                      borderBottom: `2px solid rgba(192,154,91,0.35)`,
                    }}
                  >
                    {categorie}
                  </div><div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                      gap: 14,
                    }}
                  >
                    {items.map((item) => (
                      <article
                        key={item.id}
                        style={{
                          background: BG_CARD,
                          borderRadius: 14,
                          padding: "16px 18px",
                          border: `1px solid ${BORDER}`,
                          boxShadow: "0 4px 18px rgba(61,46,32,0.06)",
                          display: "flex",
                          flexDirection: "column",
                          gap: 10,
                        }}
                      ><span
                          style={{
                            alignSelf: "flex-start",
                            display: "inline-block",
                            fontSize: 10,
                            fontWeight: 700,
                            letterSpacing: "0.05em",
                            textTransform: "uppercase",
                            padding: "4px 10px",
                            borderRadius: 999,
                            background: "rgba(192,154,91,0.18)",
                            color: "#7A5F2A",
                            border: "1px solid rgba(192,154,91,0.35)",
                          }}
                        >
                          {item.categorie}
                        </span><h3
                          style={{
                            margin: 0,
                            fontFamily: FF_PLAYFAIR,
                            fontSize: 16,
                            fontWeight: 700,
                            color: TAUPE,
                            lineHeight: 1.3,
                          }}
                        >
                          {item.titre}
                        </h3><p style={{ margin: 0, fontSize: 13, color: MUTED, lineHeight: 1.45, whiteSpace: "pre-wrap", ...LINE_CLAMP_2 }}>
                          {item.contenu}
                        </p><div style={{ fontSize: 11, color: MUTED, marginTop: "auto" }}>Créé le {item.dateCreation}</div><div style={{ display: "flex", gap: 8, marginTop: 4 }}><button
                            type="button"
                            onClick={() => setViewing(item)}
                            style={{
                              flex: 1,
                              padding: "8px 12px",
                              borderRadius: 8,
                              border: `1px solid ${BORDER}`,
                              background: "#FAF8F5",
                              color: TAUPE,
                              fontFamily: FF_DM,
                              fontSize: 12,
                              fontWeight: 600,
                              cursor: "pointer",
                            }}
                          >
                            Voir
                          </button><button
                            type="button"
                            onClick={() => supprimerFiche(item.id)}
                            style={{
                              flex: 1,
                              padding: "8px 12px",
                              borderRadius: 8,
                              border: "1px solid rgba(184,92,58,0.25)",
                              background: "rgba(184,92,58,0.06)",
                              color: "#8B4A32",
                              fontFamily: FF_DM,
                              fontSize: 12,
                              fontWeight: 600,
                              cursor: "pointer",
                            }}
                          >
                            Supprimer
                          </button></div></article>
                    ))}
                  </div></div>
              ))}
          </section></div></main>

      {/* Modal lecture */}
      {viewing && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 50,
            background: "rgba(61,46,32,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
            boxSizing: "border-box",
          }}
          onClick={() => setViewing(null)}
        ><div
            style={{
              background: BG_CARD,
              borderRadius: 16,
              maxWidth: 560,
              width: "100%",
              maxHeight: "85vh",
              overflow: "auto",
              padding: "24px 26px",
              border: `1px solid ${BORDER}`,
              boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
            }}
            onClick={(e) => e.stopPropagation()}
          ><div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 12 }}><span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                  padding: "4px 10px",
                  borderRadius: 999,
                  background: "rgba(192,154,91,0.18)",
                  color: "#7A5F2A",
                  border: "1px solid rgba(192,154,91,0.35)",
                }}
              >
                {viewing.categorie}
              </span><button
                type="button"
                onClick={() => setViewing(null)}
                style={{
                  border: "none",
                  background: "transparent",
                  color: MUTED,
                  fontSize: 22,
                  lineHeight: 1,
                  cursor: "pointer",
                  padding: 4,
                }}
                aria-label="Fermer"
              >
                ×
              </button></div><h3 style={{ margin: "0 0 8px", fontFamily: FF_PLAYFAIR, fontSize: 22, fontWeight: 700, color: TAUPE }}>{viewing.titre}</h3><div style={{ fontSize: 12, color: MUTED, marginBottom: 16 }}>Créé le {viewing.dateCreation}</div><div style={{ fontSize: 14, color: TAUPE, lineHeight: 1.55, whiteSpace: "pre-wrap", fontFamily: FF_DM }}>{viewing.contenu}</div><div style={{ marginTop: 20, display: "flex", justifyContent: "flex-end" }}><button
                type="button"
                onClick={() => setViewing(null)}
                style={{
                  padding: "10px 18px",
                  borderRadius: 10,
                  border: `1px solid ${BORDER}`,
                  background: BG,
                  color: TAUPE,
                  fontFamily: FF_DM,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Fermer
              </button></div></div></div>
      )}
    </>
  );
}
